/**
 * Dérivation de clés à partir du mot de passe maître (`docs/ARCHITECTURE.md` §1).
 *
 * ```
 * masterPassword + sel  --Argon2id-->  masterKey (256 bits)
 *   sel = kdfParams.saltBase64 (aléatoire, compte créé sans e-mail)
 *       | SHA-256(domaine | e-mail normalisé) (comptes antérieurs)
 * masterKey --HKDF-SHA256("stretch")--> stretchedMasterKey  (chiffrement, reste sur l'appareil)
 * masterKey --HKDF-SHA256("auth")-->    masterPasswordHash  (authentification serveur)
 * ```
 *
 * INVARIANTS :
 *  - Le mot de passe maître n'est ni stocké, ni transmis, ni journalisé.
 *  - `stretchedMasterKey` ne quitte JAMAIS l'appareil.
 *  - Seul `masterPasswordHash` part vers le serveur ; le serveur le re-hache
 *    (Argon2id) avant stockage — hors périmètre de ce paquet.
 *  - Paramètres Argon2id sous le plancher (`KDF_POLICY`) = refus explicite.
 */
import { argon2id, argon2idAsync } from '@noble/hashes/argon2.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  DEFAULT_KDF_PARAMS,
  KDF_DOMAIN,
  KDF_POLICY,
  KDF_SALT_LENGTH,
  MASTER_PASSWORD_HASH_LENGTH,
  SYMMETRIC_KEY_LENGTH,
  type KdfParams,
} from './constants.js';
import { assertInteger, base64ToBytes, bytesToBase64, utf8ToBytes, wipe } from './bytes.js';
import { InvalidParameterError, UnsupportedAlgorithmError, WeakKdfParametersError } from './errors.js';
import { randomBytes } from './random.js';
import { SecretKey, assertSecretKey } from './secret.js';

/** Entrées de la dérivation du `masterKey`. */
export interface DeriveMasterKeyInput {
  /** Mot de passe maître en clair — reste strictement local. */
  readonly masterPassword: string;
  /**
   * E-mail du compte. Sert de sel SEULEMENT si `params.saltBase64` est absent
   * (comptes antérieurs) ; facultatif sinon.
   */
  readonly email?: string;
  /** Paramètres Argon2id (et sel aléatoire éventuel). Défaut : `DEFAULT_KDF_PARAMS`. */
  readonly params?: KdfParams;
}

/** Jeu complet de clés dérivées d'un mot de passe maître. */
export interface AccountKeys {
  /** E-mail normalisé ; chaîne vide pour un compte sans e-mail (sel aléatoire). */
  readonly email: string;
  /** Paramètres réellement appliqués (à stocker comme métadonnée du compte). */
  readonly kdfParams: KdfParams;
  /** Clé racine Argon2id — ne sort pas de l'appareil. */
  readonly masterKey: SecretKey;
  /** Clé de chiffrement (protège la `vaultKey`) — ne sort pas de l'appareil. */
  readonly stretchedMasterKey: SecretKey;
  /** Matériel d'authentification transmis au serveur (256 bits). */
  readonly masterPasswordHash: Uint8Array;
  /** Même valeur, encodée base64 : forme envoyée sur le réseau. */
  readonly masterPasswordHashBase64: string;
}

/**
 * Valide les paramètres Argon2id contre le plancher NON NÉGOCIABLE de
 * `CLAUDE.md` : mémoire ≥ 64 Mo, itérations ≥ 3, parallélisme 1-4.
 *
 * @throws {UnsupportedAlgorithmError} algorithme autre qu'`argon2id`
 * @throws {WeakKdfParametersError} paramètre sous le plancher
 */
export function assertKdfParams(params: KdfParams): void {
  if (params === null || typeof params !== 'object') {
    throw new InvalidParameterError('kdfParams: objet attendu');
  }
  if (params.algorithm !== 'argon2id') {
    throw new UnsupportedAlgorithmError(
      `KDF non autorise: ${String(params.algorithm)} (argon2id uniquement)`,
    );
  }
  assertInteger(params.memoryKiB, 'kdfParams.memoryKiB', 1, KDF_POLICY.maxMemoryKiB);
  assertInteger(params.iterations, 'kdfParams.iterations', 1, KDF_POLICY.maxIterations);
  assertInteger(params.parallelism, 'kdfParams.parallelism', 1, 0xffffff);

  if (params.memoryKiB < KDF_POLICY.minMemoryKiB) {
    throw new WeakKdfParametersError(
      `memoryKiB trop faible: ${params.memoryKiB} < ${KDF_POLICY.minMemoryKiB} (64 Mo minimum)`,
    );
  }
  if (params.iterations < KDF_POLICY.minIterations) {
    throw new WeakKdfParametersError(
      `iterations trop faible: ${params.iterations} < ${KDF_POLICY.minIterations}`,
    );
  }
  if (
    params.parallelism < KDF_POLICY.minParallelism ||
    params.parallelism > KDF_POLICY.maxParallelism
  ) {
    throw new WeakKdfParametersError(
      `parallelism hors plage: ${params.parallelism} (attendu ${KDF_POLICY.minParallelism}-${KDF_POLICY.maxParallelism})`,
    );
  }
  // Contrainte Argon2id (RFC 9106 §3.1) : m >= 8p.
  if (params.memoryKiB < 8 * params.parallelism) {
    throw new WeakKdfParametersError('memoryKiB doit valoir au moins 8 x parallelism');
  }
  if (params.saltBase64 !== undefined) decodeKdfSalt(params.saltBase64);
}

/** Sel aléatoire d'un compte : exactement {@link KDF_SALT_LENGTH} octets, sinon refus. */
function decodeKdfSalt(saltBase64: unknown): Uint8Array {
  if (typeof saltBase64 !== 'string') {
    throw new InvalidParameterError('kdfParams.saltBase64: chaine base64 attendue');
  }
  const salt = base64ToBytes(saltBase64);
  if (salt.length !== KDF_SALT_LENGTH) {
    throw new InvalidParameterError(`kdfParams.saltBase64: ${KDF_SALT_LENGTH} octets attendus`);
  }
  return salt;
}

/**
 * Sel Argon2id ALÉATOIRE d'un nouveau compte créé sans e-mail : 32 octets du
 * CSPRNG, à ranger dans `kdfParams.saltBase64` (`docs/ARCHITECTURE.md` §1).
 */
export function generateKdfSaltBase64(): string {
  return bytesToBase64(randomBytes(KDF_SALT_LENGTH));
}

/**
 * Sel effectif et e-mail rendu : le sel aléatoire des paramètres l'emporte ;
 * sans lui, l'e-mail est OBLIGATOIRE (il est le sel des comptes antérieurs).
 */
function resolveSalt(email: string | undefined, params: KdfParams): { readonly email: string; readonly salt: Uint8Array } {
  if (params.saltBase64 === undefined) {
    const normalized = normalizeEmail(email as string);
    return { email: normalized, salt: deriveKdfSalt(normalized) };
  }
  const salt = decodeKdfSalt(params.saltBase64);
  return { email: email === undefined || email.trim() === '' ? '' : normalizeEmail(email), salt };
}

/**
 * Normalise l'e-mail servant de sel : suppression des espaces de bordure,
 * NFC, minuscules. Deux appareils saisissant le même e-mail avec une
 * casse ou une normalisation Unicode différente doivent dériver la MÊME clé.
 */
export function normalizeEmail(email: string): string {
  if (typeof email !== 'string') {
    throw new InvalidParameterError('email: chaine attendue');
  }
  const normalized = email.normalize('NFC').trim().toLowerCase();
  if (normalized.length === 0) {
    throw new InvalidParameterError('email: sel de derivation manquant');
  }
  return normalized;
}

/**
 * Normalise le mot de passe maître : NFC, sans repli de casse ni rognage
 * (esprit du profil `OpaqueString` de la RFC 8265). Un même mot de passe saisi
 * sur iOS, Android ou desktop doit produire les mêmes octets UTF-8.
 *
 * ⚠️ Aucun rognage : les espaces de bordure sont du matériel de mot de passe.
 */
export function normalizeMasterPassword(masterPassword: string): string {
  if (typeof masterPassword !== 'string') {
    throw new InvalidParameterError('masterPassword: chaine attendue');
  }
  if (masterPassword.length === 0) {
    throw new InvalidParameterError('masterPassword: mot de passe maitre manquant');
  }
  return masterPassword.normalize('NFC');
}

/**
 * Sel Argon2id = `SHA-256(domaine || e-mail normalisé)`, soit 32 octets.
 *
 * Hacher l'e-mail garantit (a) une longueur de sel toujours conforme (les
 * e-mails courts feraient moins de 8 octets), (b) une séparation de domaine
 * empêchant la réutilisation du sel par un autre produit SANTVIA.
 */
export function deriveKdfSalt(email: string): Uint8Array {
  return sha256(utf8ToBytes(`${KDF_DOMAIN.salt}|${normalizeEmail(email)}`));
}

function argon2Options(params: KdfParams) {
  return {
    t: params.iterations,
    m: params.memoryKiB,
    p: params.parallelism,
    dkLen: SYMMETRIC_KEY_LENGTH,
  };
}

function prepare(input: DeriveMasterKeyInput, checkPolicy: boolean) {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  const params = input.params ?? DEFAULT_KDF_PARAMS;
  if (checkPolicy) {
    assertKdfParams(params);
  } else if (params.algorithm !== 'argon2id') {
    throw new UnsupportedAlgorithmError('KDF non autorise (argon2id uniquement)');
  }
  const { email, salt } = resolveSalt(input.email, params);
  const password = utf8ToBytes(normalizeMasterPassword(input.masterPassword));
  return { params, email, password, salt };
}

/**
 * Dérive le `masterKey` (256 bits) par Argon2id.
 *
 * Opération volontairement coûteuse (~64 Mo, 3 passes) : c'est ce qui rend une
 * attaque hors ligne sur le mot de passe maître économiquement prohibitive.
 * Sur un thread UI, préférer {@link deriveMasterKeyAsync}.
 */
export function deriveMasterKey(input: DeriveMasterKeyInput): SecretKey {
  const { params, password, salt } = prepare(input, true);
  try {
    return SecretKey.adopt(
      argon2id(password, salt, argon2Options(params)),
      'masterKey',
      SYMMETRIC_KEY_LENGTH,
    );
  } finally {
    // Le tampon du mot de passe est effacé même si Argon2id échoue.
    wipe(password);
  }
}

/**
 * Variante asynchrone : rend la main à la boucle d'événements pendant le
 * calcul, pour ne pas figer l'interface pendant le déverrouillage.
 */
export async function deriveMasterKeyAsync(input: DeriveMasterKeyInput): Promise<SecretKey> {
  const { params, password, salt } = prepare(input, true);
  try {
    const bytes = await argon2idAsync(password, salt, argon2Options(params));
    return SecretKey.adopt(bytes, 'masterKey', SYMMETRIC_KEY_LENGTH);
  } finally {
    wipe(password);
  }
}

/**
 * `stretchedMasterKey = HKDF-SHA256(masterKey, info = "…|stretched-master-key")`.
 * Clé de chiffrement de la protected symmetric key. Ne quitte pas l'appareil.
 */
export function deriveStretchedMasterKey(masterKey: SecretKey): SecretKey {
  assertSecretKey(masterKey, 'masterKey', SYMMETRIC_KEY_LENGTH);
  const bytes = hkdf(
    sha256,
    masterKey.reveal(),
    undefined,
    utf8ToBytes(KDF_DOMAIN.stretchedMasterKey),
    SYMMETRIC_KEY_LENGTH,
  );
  return SecretKey.adopt(bytes, 'stretchedMasterKey', SYMMETRIC_KEY_LENGTH);
}

/**
 * `masterPasswordHash = HKDF-SHA256(masterKey, info = "…|master-password-hash")`.
 *
 * Seule valeur dérivée du mot de passe maître autorisée à quitter l'appareil.
 * La séparation de domaine HKDF garantit qu'elle ne révèle RIEN de
 * `stretchedMasterKey` : un serveur compromis ne peut pas déchiffrer le coffre.
 */
export function deriveMasterPasswordHash(masterKey: SecretKey): Uint8Array {
  assertSecretKey(masterKey, 'masterKey', SYMMETRIC_KEY_LENGTH);
  return hkdf(
    sha256,
    masterKey.reveal(),
    undefined,
    utf8ToBytes(KDF_DOMAIN.masterPasswordHash),
    MASTER_PASSWORD_HASH_LENGTH,
  );
}

function assembleAccountKeys(email: string, kdfParams: KdfParams, masterKey: SecretKey): AccountKeys {
  const stretchedMasterKey = deriveStretchedMasterKey(masterKey);
  const masterPasswordHash = deriveMasterPasswordHash(masterKey);
  return {
    email,
    kdfParams,
    masterKey,
    stretchedMasterKey,
    masterPasswordHash,
    masterPasswordHashBase64: bytesToBase64(masterPasswordHash),
  };
}

/** Dérive en une passe `masterKey`, `stretchedMasterKey` et `masterPasswordHash`. */
export function deriveAccountKeys(input: DeriveMasterKeyInput): AccountKeys {
  const kdfParams = input.params ?? DEFAULT_KDF_PARAMS;
  const { email } = resolveSalt(input.email, kdfParams);
  return assembleAccountKeys(email, kdfParams, deriveMasterKey(input));
}

/** Variante asynchrone de {@link deriveAccountKeys}. */
export async function deriveAccountKeysAsync(input: DeriveMasterKeyInput): Promise<AccountKeys> {
  const kdfParams = input.params ?? DEFAULT_KDF_PARAMS;
  const { email } = resolveSalt(input.email, kdfParams);
  return assembleAccountKeys(email, kdfParams, await deriveMasterKeyAsync(input));
}

/** Efface le matériel de clé d'un `AccountKeys` (fin de session). */
export function destroyAccountKeys(keys: AccountKeys): void {
  keys.masterKey.destroy();
  keys.stretchedMasterKey.destroy();
  wipe(keys.masterPasswordHash);
}

/**
 * Dérivation SANS contrôle de politique — tests et bancs de mesure UNIQUEMENT.
 * Réexportée sous le nom `unsafeDeriveMasterKey` (voir `unsafe.ts`).
 */
export function deriveMasterKeyUnchecked(input: DeriveMasterKeyInput): SecretKey {
  const { params, password, salt } = prepare(input, false);
  try {
    return SecretKey.adopt(
      argon2id(password, salt, argon2Options(params)),
      'masterKey',
      SYMMETRIC_KEY_LENGTH,
    );
  } finally {
    wipe(password);
  }
}
